/**
 * Accordion Module
 * Rozwijanie/zwijanie elementów akordeonu z gładką animacją max-height.
 * Konfiguracja przez atrybuty data-*:
 *
 * data-accordion         — marker inicjalizacji (wymagany)
 * data-accordion-single  — (opcjonalny) tylko jeden element otwarty na raz
 *
 * Użycie HTML:
 *   <div class="accordion" data-accordion>
 *     <div class="accordion__item">
 *       <button class="accordion__header" aria-expanded="false" aria-controls="acc-1">
 *         <span class="accordion__title">Pytanie</span>
 *         <svg class="accordion__icon" ...>...</svg>
 *       </button>
 *       <div class="accordion__body" id="acc-1" role="region">
 *         <div class="accordion__content">Odpowiedź</div>
 *       </div>
 *     </div>
 *   </div>
 */

(function () {
  'use strict';

  var SELECTOR = '.accordion[data-accordion]';

  /**
   * Zamyka element akordeonu
   */
  function closeItem(item) {
    var header = item.querySelector('.accordion__header');
    var body = item.querySelector('.accordion__body');
    if (!body) return;

    // Ustaw max-height na aktualną wartość scrollHeight, żeby transition zadziałał
    body.style.maxHeight = body.scrollHeight + 'px';

    // Force reflow
    body.offsetHeight; // eslint-disable-line no-unused-expressions

    // Rozpocznij animację zamykania
    requestAnimationFrame(function () {
      body.style.maxHeight = '0px';
    });

    item.classList.remove('is-open');
    if (header) header.setAttribute('aria-expanded', 'false');
  }

  /**
   * Otwiera element akordeonu
   */
  function openItem(item) {
    var header = item.querySelector('.accordion__header');
    var body = item.querySelector('.accordion__body');
    if (!body) return;

    item.classList.add('is-open');
    if (header) header.setAttribute('aria-expanded', 'true');

    // Ustaw max-height na scrollHeight dla animacji
    body.style.maxHeight = body.scrollHeight + 'px';

    // Po zakończeniu transition — usuń inline max-height (pozwala na dynamiczną zmianę zawartości)
    function onTransitionEnd(e) {
      if (e.propertyName === 'max-height' && item.classList.contains('is-open')) {
        body.style.maxHeight = 'none';
      }
      body.removeEventListener('transitionend', onTransitionEnd);
    }
    body.addEventListener('transitionend', onTransitionEnd);
  }

  /**
   * Toggle elementu
   */
  function toggleItem(accordion, item) {
    var isOpen = item.classList.contains('is-open');
    var isSingle = accordion.hasAttribute('data-accordion-single');

    // Tryb single — zamknij inne otwarte
    if (isSingle && !isOpen) {
      var openItems = accordion.querySelectorAll('.accordion__item.is-open');
      for (var i = 0; i < openItems.length; i++) {
        closeItem(openItems[i]);
      }
    }

    if (isOpen) {
      closeItem(item);
    } else {
      openItem(item);
    }
  }

  /**
   * Ustawia atrybuty dostępności
   */
  function setupAccessibility(accordion) {
    var items = accordion.querySelectorAll('.accordion__item');

    items.forEach(function (item, index) {
      var header = item.querySelector('.accordion__header');
      var body = item.querySelector('.accordion__body');

      if (!header || !body) return;

      // Generuj unikalne ID jeśli brak
      var bodyId = body.id || 'accordion-body-' + Date.now() + '-' + index;
      body.id = bodyId;

      header.setAttribute('aria-expanded', 'false');
      header.setAttribute('aria-controls', bodyId);
      body.setAttribute('role', 'region');
      body.setAttribute('aria-labelledby', header.id || '');
    });
  }

  /**
   * Inicjalizacja pojedynczego akordeonu
   */
  function initAccordion(accordion) {
    setupAccessibility(accordion);

    accordion.addEventListener('click', function (e) {
      // Znajdź kliknięty header (event delegation)
      var header = e.target.closest('.accordion__header');
      if (!header) return;

      var item = header.closest('.accordion__item');
      if (!item) return;

      e.preventDefault();
      toggleItem(accordion, item);
    });

    // Obsługa klawiatury
    accordion.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;

      var header = e.target.closest('.accordion__header');
      if (!header) return;

      var item = header.closest('.accordion__item');
      if (!item) return;

      e.preventDefault();
      toggleItem(accordion, item);
    });
  }

  /**
   * Scroll do elementu i otwarcie go na podstawie URL hash
   * Np. faq.html#jakistemat → scrolluje do #jakistemat i otwiera
   */
  function scrollToHash() {
    var hash = window.location.hash;
    if (!hash) return;

    var targetId = hash.substring(1); // usuń #
    var targetItem = document.getElementById(targetId);
    if (!targetItem) return;

    // Sprawdź czy to accordion__item
    if (!targetItem.classList.contains('accordion__item')) {
      // Może to element wewnątrz accordion__item
      targetItem = targetItem.closest('.accordion__item');
      if (!targetItem) return;
    }

    var accordion = targetItem.closest(SELECTOR);
    if (!accordion) return;

    // Otwórz element (jeśli zamknięty)
    if (!targetItem.classList.contains('is-open')) {
      // W trybie single zamknij najpierw inne
      if (accordion.hasAttribute('data-accordion-single')) {
        var openItems = accordion.querySelectorAll('.accordion__item.is-open');
        for (var i = 0; i < openItems.length; i++) {
          closeItem(openItems[i]);
        }
      }
      openItem(targetItem);
    }

    // Smooth scroll do elementu z małym opóźnieniem (żeby animacja otwarcia się rozpoczęła)
    setTimeout(function () {
      targetItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  /**
   * Inicjalizacja wszystkich akordeonów na stronie
   */
  function init() {
    var accordions = document.querySelectorAll(SELECTOR);
    if (!accordions.length) return;

    accordions.forEach(function (accordion) {
      initAccordion(accordion);
    });

    // Scroll do hash po inicjalizacji
    scrollToHash();

    // Reaguj na zmianę hash (kliknięcie linku z anchorem na tej samej stronie)
    window.addEventListener('hashchange', scrollToHash);
  }

  // Inicjalizacja po załadowaniu DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
